<template>
  <v-dialog :value="value" max-width="700" @input="$emit('input', $event)">
    <v-card :loading="isLoading">
      <v-card-title class="text-h6">
        {{ dialogTitle }}
      </v-card-title>
      <v-card-text>
        <div class="mb-4">
          {{ $t("FIELD_TAGS.CURRENT_TAG") }}:
          <strong>{{ fieldTag ? fieldTag.value : "-" }}</strong>
        </div>

        <v-alert v-if="!mergeSuggestions.length && !isLoading" dense outlined type="info">
          {{ $t("FIELD_TAGS.NO_MERGE_SUGGESTIONS") }}
        </v-alert>

        <v-autocomplete
          v-if="mode === 'into-current' && mergeOptions.length"
          v-model="selectedSourceTagIds"
          :search-input.sync="sourceTagsSearch"
          :items="mergeOptions"
          item-value="id"
          item-text="value"
          multiple
          chips
          small-chips
          deletable-chips
          hide-selected
          outlined
          dense
          clearable
          :label="$t('FIELD_TAGS.SELECT_TAGS_TO_MERGE_INTO_CURRENT')"
        >
          <template v-slot:item="{ item }">
            <v-list-item-content>
              <v-list-item-title>{{ getSuggestionLabel(item) }}</v-list-item-title>
            </v-list-item-content>
          </template>
        </v-autocomplete>

        <v-autocomplete
          v-if="mode === 'current-into' && mergeOptions.length"
          v-model="selectedTargetTagId"
          :search-input.sync="targetTagsSearch"
          :items="mergeOptions"
          item-value="id"
          item-text="value"
          outlined
          dense
          clearable
          :label="$t('FIELD_TAGS.SELECT_TARGET_TAG')"
        >
          <template v-slot:item="{ item }">
            <v-list-item-content>
              <v-list-item-title>{{ getSuggestionLabel(item) }}</v-list-item-title>
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close">{{ $t("FIELD_TAGS.CANCEL_BUTTON") }}</v-btn>
        <v-btn color="success" :disabled="!canMerge" :loading="isMerging" @click="submitMerge">
          {{ $t("FIELD_TAGS.MERGE_BUTTON") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { SHOW_SNACK } from "@/store/snack.module";
import {
  GET_FIELD_TAG_OPTIONS,
  GET_FIELD_TAG_MERGE_SUGGESTIONS,
  MERGE_FIELD_TAGS,
} from "@/store/fieldTags.module";

export default {
  name: "MergeFieldTagsDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: null,
    },
    fieldTag: {
      type: Object,
      default: null,
    },
    fieldTagTypeId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      mergeSuggestions: [],
      allTypeTags: [],
      selectedSourceTagIds: [],
      selectedTargetTagId: null,
      sourceTagsSearch: "",
      targetTagsSearch: "",
      isLoading: false,
      isMerging: false,
    };
  },
  computed: {
    dialogTitle() {
      if (this.mode === "into-current") {
        return this.$t("FIELD_TAGS.MERGE_INTO_CURRENT_TITLE");
      }

      if (this.mode === "current-into") {
        return this.$t("FIELD_TAGS.MERGE_CURRENT_INTO_OTHER_TITLE");
      }

      return this.$t("FIELD_TAGS.MERGE_TAGS_TITLE");
    },
    canMerge() {
      if (!this.fieldTag || this.isLoading || this.isMerging) {
        return false;
      }

      if (this.mode === "into-current") {
        return this.selectedSourceTagIds.length > 0;
      }

      if (this.mode === "current-into") {
        return Boolean(this.selectedTargetTagId);
      }

      return false;
    },
    mergeOptions() {
      const suggestionsById = this.mergeSuggestions.reduce((acc, suggestion) => {
        acc[suggestion.id] = suggestion.similarity_percent;
        return acc;
      }, {});

      return (this.allTypeTags || [])
        .filter((tag) => tag.id !== this.fieldTag?.id)
        .map((tag) => ({
          id: tag.id,
          value: tag.value,
          similarity_percent: suggestionsById[tag.id] || null,
          is_suggested: Boolean(suggestionsById[tag.id]),
        }))
        .sort((a, b) => {
          const aScore = a.similarity_percent || -1;
          const bScore = b.similarity_percent || -1;

          if (aScore !== bScore) {
            return bScore - aScore;
          }

          return String(a.value).localeCompare(String(b.value));
        });
    },
    resolvedFieldTagTypeId() {
      return this.fieldTag?.field_tag_type_id || this.fieldTagTypeId;
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.loadData();
        return;
      }

      this.resetState();
    },
  },
  methods: {
    getErrorMessage(error) {
      if (error && error.errors && typeof error.errors === "object") {
        const firstErrorField = Object.keys(error.errors)[0];
        const firstErrorList = error.errors[firstErrorField];
        if (Array.isArray(firstErrorList) && firstErrorList.length) {
          return firstErrorList[0];
        }
      }
      if (error && error.message) {
        return error.message;
      }
      return this.$t("GENERAL.UNEXPECTED_ERROR");
    },
    getSuggestionLabel(item) {
      if (item.is_suggested && item.similarity_percent) {
        return `${item.value} (${item.similarity_percent}% match - suggested)`;
      }

      return item.value;
    },
    resetState() {
      this.mergeSuggestions = [];
      this.allTypeTags = [];
      this.selectedSourceTagIds = [];
      this.selectedTargetTagId = null;
      this.sourceTagsSearch = "";
      this.targetTagsSearch = "";
      this.isLoading = false;
      this.isMerging = false;
    },
    close() {
      this.$emit("input", false);
    },
    loadData() {
      if (!this.fieldTag) {
        return;
      }

      this.resetState();
      this.isLoading = true;

      Promise.all([
        this.$store.dispatch(GET_FIELD_TAG_MERGE_SUGGESTIONS, this.fieldTag.id),
        this.$store.dispatch(GET_FIELD_TAG_OPTIONS, {
          field_tag_type_id: this.resolvedFieldTagTypeId,
        }),
      ])
        .then(([suggestions, allTypeTags]) => {
          this.mergeSuggestions = suggestions || [];
          this.allTypeTags = allTypeTags || [];

          if (this.mode === "current-into" && this.mergeOptions.length) {
            this.selectedTargetTagId = this.mergeOptions[0].id;
          }
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            type: "error",
            message: this.getErrorMessage(error),
          });
          this.close();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    submitMerge() {
      if (!this.canMerge) {
        return;
      }

      const targetTagId = this.mode === "into-current" ? this.fieldTag.id : this.selectedTargetTagId;
      const sourceTagIds = this.mode === "into-current" ? this.selectedSourceTagIds : [this.fieldTag.id];

      this.isMerging = true;
      this.$store
        .dispatch(MERGE_FIELD_TAGS, {
          target_tag_id: targetTagId,
          source_tag_ids: sourceTagIds,
        })
        .then(() => {
          this.$store.dispatch(SHOW_SNACK, { message: this.$t("GENERAL.SUCCESS_MESSAGE") });
          this.$emit("merged");
          this.close();
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            type: "error",
            message: this.getErrorMessage(error),
          });
        })
        .finally(() => {
          this.isMerging = false;
        });
    },
  },
};
</script>
